import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../../../shared/components/PageHeader';
import { Spinner } from '../../../shared/components/Spinner';
import { ErrorAlert } from '../../../shared/components/ErrorAlert';
import { useDocumentTitle } from '../../../shared/hooks/useDocumentTitle';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchMerchantById } from '../../../services/merchants/merchantApi';
import { updateMerchant, type UpdateMerchantBody } from '../../../services/merchants/adminMerchantApi';
import type { Merchant } from '../../../models/merchant';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type VisibleKey = keyof UpdateMerchantBody;

interface VisibleFields {
  email: boolean;
  merchantEmail: boolean;
  phone: boolean;
  addressLine1: boolean;
  addressLine2: boolean;
  city: boolean;
  firstName: boolean;
  lastName: boolean;
  banned: boolean;
  company: boolean;
}

function deriveVisible(m: Merchant | null): VisibleFields | null {
  if (!m) {
    return null;
  }
  const mc = m.Merchant;
  return {
    email: m.Email !== undefined,
    merchantEmail: Boolean(mc && mc.Email !== undefined),
    phone: Boolean(mc && mc.Phone !== undefined),
    addressLine1: Boolean(mc && mc.AddressLine1 !== undefined),
    addressLine2: Boolean(mc && mc.AddressLine2 !== undefined),
    city: Boolean(mc && mc.City !== undefined),
    firstName: m.FirstName !== undefined,
    lastName: m.LastName !== undefined,
    banned: typeof m.Banned === 'boolean',
    company: mc?.Name !== undefined,
  };
}

function initialValues(m: Merchant, v: VisibleFields): Record<string, string | boolean> {
  const mc = m.Merchant;
  const out: Record<string, string | boolean> = {};
  if (v.email) {
    out.email = m.Email ?? '';
  }
  if (v.merchantEmail && mc) {
    out.merchantEmail = mc.Email ?? '';
  }
  if (v.phone && mc) {
    out.phone = mc.Phone ?? '';
  }
  if (v.addressLine1 && mc) {
    out.addressLine1 = mc.AddressLine1 ?? '';
  }
  if (v.addressLine2 && mc) {
    out.addressLine2 = mc.AddressLine2 ?? '';
  }
  if (v.city && mc) {
    out.city = mc.City ?? '';
  }
  if (v.firstName) {
    out.firstName = m.FirstName ?? '';
  }
  if (v.lastName) {
    out.lastName = m.LastName ?? '';
  }
  if (v.banned) {
    out.banned = Boolean(m.Banned);
  }
  if (v.company && mc) {
    out.company = mc.Name ?? '';
  }
  return out;
}

function str(values: Record<string, string | boolean>, key: string): string {
  const raw = values[key];
  return typeof raw === 'string' ? raw.trim() : '';
}

function buildPutBody(
  values: Record<string, string | boolean>,
  v: VisibleFields
): UpdateMerchantBody {
  const body: UpdateMerchantBody = {};
  if (v.email) {
    body.email = str(values, 'email');
  }
  if (v.merchantEmail) {
    body.merchantEmail = str(values, 'merchantEmail');
  }
  if (v.phone) {
    body.phone = str(values, 'phone');
  }
  if (v.addressLine1) {
    body.addressLine1 = str(values, 'addressLine1');
  }
  if (v.addressLine2) {
    body.addressLine2 = str(values, 'addressLine2');
  }
  if (v.city) {
    body.city = str(values, 'city');
  }
  if (v.firstName) {
    body.firstName = str(values, 'firstName');
  }
  if (v.lastName) {
    body.lastName = str(values, 'lastName');
  }
  if (v.banned) {
    body.banned = Boolean(values.banned);
  }
  if (v.company) {
    body.company = str(values, 'company');
  }
  return body;
}

const FIELD_LABELS: Partial<Record<VisibleKey, string>> = {
  email: 'Email',
  merchantEmail: 'Merchant email',
  phone: 'Phone',
  addressLine1: 'Address line 1',
  addressLine2: 'Address line 2',
  city: 'City',
  firstName: 'First name',
  lastName: 'Last name',
  banned: 'Banned',
  company: 'Company Name',
};

export function MerchantEditPage(): React.ReactElement {
  const { merchantId } = useParams<{ merchantId: string }>();
  const navigate = useNavigate();
  const { acquireAccessToken } = useAuth();
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState<Record<string, string | boolean>>({});
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<string, string>>>({});

  const visible = useMemo(() => deriveVisible(merchant), [merchant]);

  const load = useCallback(async () => {
    if (!merchantId) {
      setLoadError('Missing merchant id.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchMerchantById(merchantId, acquireAccessToken);
      setMerchant(data);
      const v = deriveVisible(data);
      if (v) {
        setValues(initialValues(data, v));
      }
    } catch (e) {
      setMerchant(null);
      setLoadError(e instanceof Error ? e.message : 'Failed to load merchant.');
    } finally {
      setLoading(false);
    }
  }, [merchantId, acquireAccessToken]);

  useEffect(() => {
    void load();
  }, [load]);

  useDocumentTitle('Edit merchant · Triplicity Backoffice');

  const validate = useCallback((): boolean => {
    if (!visible) {
      return false;
    }
    const next: Partial<Record<string, string>> = {};
    const checkEmail = (key: 'email' | 'merchantEmail', label: string): void => {
      const raw = values[key];
      const s = typeof raw === 'string' ? raw.trim() : '';
      if (!s) {
        next[key] = `${label} is required.`;
      } else if (!EMAIL_RE.test(s)) {
        next[key] = 'Enter a valid email address.';
      }
    };
    if (visible.email) {
      checkEmail('email', 'Email');
    }
    if (visible.merchantEmail) {
      checkEmail('merchantEmail', 'Merchant email');
    }
    const strFields: [VisibleKey, string][] = [
      ['phone', 'Phone'],
      ['addressLine1', 'Address line 1'],
      ['addressLine2', 'Address line 2'],
      ['city', 'City'],
      ['firstName', 'First name'],
      ['lastName', 'Last name'],
      ['company', 'Company Name'],
    ];
    for (const [key, label] of strFields) {
      if (visible[key]) {
        const raw = values[key];
        const s = typeof raw === 'string' ? raw.trim() : '';
        if (!s) {
          next[key] = `${label} is required.`;
        }
      }
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  }, [visible, values]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!merchantId || !visible) {
      return;
    }
    setSubmitError(null);
    if (!validate()) {
      return;
    }
    const body = buildPutBody(values, visible);
    setSaving(true);
    try {
      await updateMerchant(merchantId, body, acquireAccessToken);
      navigate(`/merchants/${encodeURIComponent(merchantId)}`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  const setStr = (key: string, v: string): void => {
    setValues((prev) => ({ ...prev, [key]: v }));
    setFieldErrors((prev) => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  };

  return (
    <div className="bo-detail-page">
      <PageHeader
        title="Edit merchant"
        subtitle={merchantId ? `Id: ${merchantId}` : undefined}
        actions={
          <div className="bo-actions">
            <button
              type="button"
              className="bo-btn bo-btn-secondary"
              onClick={() =>
                merchantId
                  ? navigate(`/merchants/${encodeURIComponent(merchantId)}`)
                  : navigate('/merchants')
              }
            >
              Cancel
            </button>
          </div>
        }
      />

      <Link className="bo-detail-back-link" to={merchantId ? `/merchants/${merchantId}` : '/merchants'}>
        ← Back to merchant
      </Link>

      {loading ? (
        <div className="bo-card bo-detail-loading">
          <Spinner label="Loading merchant…" />
        </div>
      ) : null}

      {!loading && loadError ? <ErrorAlert message={loadError} /> : null}

      {!loading && !loadError && merchant && visible ? (
        <form className="bo-card bo-form-card" onSubmit={(e) => void handleSubmit(e)}>
          <h2 className="bo-form-section-title">Editable fields</h2>
          <p className="bo-form-hint">
            Only fields returned for this record are shown. All visible fields are required.
          </p>
          <div className="bo-form-fields">
            {(
              [
                'email',
                'merchantEmail',
                'phone',
                'addressLine1',
                'addressLine2',
                'city',
                'firstName',
                'lastName',
                'company',
              ] as const
            ).map((key) =>
              visible[key] ? (
                <div key={key} className="bo-field">
                  <label className="bo-field-label" htmlFor={`edit-${key}`}>
                    {FIELD_LABELS[key] ?? key}
                  </label>
                  <input
                    id={`edit-${key}`}
                    className="bo-input"
                    type={key === 'email' || key === 'merchantEmail' ? 'email' : 'text'}
                    value={(() => {
                      const raw = values[key];
                      return typeof raw === 'string' ? raw : '';
                    })()}
                    onChange={(ev) => setStr(key, ev.target.value)}
                    autoComplete="off"
                  />
                  {fieldErrors[key] ? (
                    <span className="bo-field-error" role="alert">
                      {fieldErrors[key]}
                    </span>
                  ) : null}
                </div>
              ) : null
            )}
            {visible.banned ? (
              <div className="bo-field bo-field-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={Boolean(values.banned)}
                    onChange={(ev) =>
                      setValues((prev) => ({ ...prev, banned: ev.target.checked }))
                    }
                  />{' '}
                  {FIELD_LABELS.banned}
                </label>
              </div>
            ) : null}
          </div>
          {submitError ? <ErrorAlert message={submitError} /> : null}
          <div className="bo-actions" style={{ marginTop: '1.25rem' }}>
            <button type="submit" className="bo-btn bo-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      ) : null}

      {!loading && !loadError && !merchant ? (
        <div className="bo-card">
          <p style={{ margin: 0 }}>Merchant not found.</p>
        </div>
      ) : null}
    </div>
  );
}
